import { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import api from "utils/api";
import { toast } from "react-toastify";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatStatus = (status, isActive) => {
  if (!status) return "-";
  const normalized = status.replace(/_/g, " ");
  return isActive ? `${normalized} (Active)` : normalized;
};

function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchStudents = async () => {
      try {
        setLoading(true);
        const response = await api.get("/student");
        if (!isMounted) return;

        const payload = Array.isArray(response?.data?.data) ? response.data.data : [];
        setStudents(payload);
        setError("");
      } catch (fetchError) {
        if (!isMounted) return;
        const message =
          fetchError?.response?.data?.message || fetchError?.message || "Failed to load students";
        setError(message);
        setStudents([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStudents();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredStudents = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) return students;

    return students.filter((student) => {
      const bucket = [
        student.name,
        student.email,
        student.parentId?.fullName,
        student.parentId?.phone,
        student.class?.name,
        student.board?.name,
      ];

      return bucket.some((value) => value && String(value).toLowerCase().includes(trimmed));
    });
  }, [students, searchQuery]);

  // Delete handlers
  const handleDeleteOpen = (student) => {
    console.log("Delete button clicked for student:", student);
    setStudentToDelete(student);
    setTimeout(() => setDeleteDialogOpen(true), 0);
  };

  const handleDeleteClose = () => {
    setStudentToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!studentToDelete?._id) return;

    setDeleting(true);
    try {
      const response = await api.delete(`/student/${studentToDelete._id}`);

      if (response.data.success) {
        toast.success(response.data.message || "Student deleted successfully");

        // Refresh the students list
        const refreshResponse = await api.get("/student");
        const payload = Array.isArray(refreshResponse?.data?.data) ? refreshResponse.data.data : [];
        setStudents(payload);
      } else {
        toast.error(response.data.message || "Failed to delete student");
      }
    } catch (err) {
      console.error("Error deleting student:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to delete student");
    } finally {
      setDeleting(false);
      handleDeleteClose();
    }
  };

  const columns = useMemo(
    () => [
      { name: "serial", align: "center", width: "6%" },
      { name: "student", align: "left" },
      { name: "parent", align: "left" },
      { name: "contact", align: "left" },
      { name: "className", align: "center" },
      { name: "boardName", align: "center" },
      { name: "subscription", align: "center" },
      { name: "createdAt", align: "center", width: "16%" },
      { name: "action", align: "center", width: "8%" },
    ],
    []
  );

  const rows = useMemo(
    () =>
      filteredStudents.map((student, index) => ({
        hasBorder: true,
        serial: index + 1,
        student: student.name || "-",
        parent: student.parentId?.fullName || "-",
        contact: student.parentId?.phone || student.phone || "-",
        className: student.class?.name || "-",
        boardName: student.board?.name || "-",
        subscription: formatStatus(student.subscriptionStatus, student.hasActiveSubscription),
        createdAt: formatDate(student.createdAt),
        action: (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteOpen(student);
            }}
            size="small"
            sx={{
              color: "#ff4d4f",
              "&:hover": { backgroundColor: "rgba(255, 77, 79, 0.08)" },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      })),
    [filteredStudents]
  );

  const body = () => {
    if (loading) {
      return (
        <ArgonBox py={6} display="flex" justifyContent="center">
          <CircularProgress size={40} />
        </ArgonBox>
      );
    }

    if (error) {
      return (
        <ArgonBox py={2} px={2}>
          <Alert severity="error">{error}</Alert>
        </ArgonBox>
      );
    }

    if (rows.length === 0) {
      return (
        <ArgonBox py={6} textAlign="center">
          <ArgonTypography variant="button" color="secondary">
            No students found.
          </ArgonTypography>
        </ArgonBox>
      );
    }

    return <Table columns={columns} rows={rows} />;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3} px={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <ArgonBox
                px={3}
                py={3}
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                gap={2}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
              >
                <ArgonTypography variant="h5" fontWeight="medium">
                  Students
                </ArgonTypography>
                <ArgonInput
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by student, parent, class, or board"
                  size="large"
                  sx={{ minWidth: { xs: "100%", md: "260px" } }}
                />
              </ArgonBox>
              <ArgonBox px={3} pb={3}>
                {body()}
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Student"
        description={`Are you sure you want to delete ${
          studentToDelete?.name || "this student"
        }? This action cannot be undone.`}
        confirmText={deleting ? "Deleting..." : "Delete Student"}
        cancelText="Cancel"
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Students;
